import { Trans, useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  WarningAmberRounded,
  PersonOffRounded,
  CheckCircleOutlineRounded,
  LockPersonRounded,
  SchoolRounded,
  AccountBalanceWalletRounded,
  AssignmentLateRounded,
} from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import { errorText } from '../../../lib/error-text';
import {
  admissionsApi,
  type WithdrawalInstallmentImpactDto,
} from '../../../lib/api/admissions.api';
import { formatMoney, formatNumber } from '../../../lib/i18n-format';

interface WithdrawalPreview {
  retainedAmount: number;
  refundAmount: number;
  zeroedInstallmentsCount: number;
  tuitionPlansWithdrawn: number;
  enrollmentsDropped: number;
  installmentImpacts: WithdrawalInstallmentImpactDto[];
}

// ─── Types ──────────────────────────────────────────────────────────────────

export type WithdrawalType = 'voluntary' | 'administrative' | 'disciplinary' | 'academic';

const WITHDRAWAL_TYPE_LABELS: Record<WithdrawalType, string> = {
  voluntary: 'Retiro Voluntario',
  administrative: 'Retiro Administrativo',
  disciplinary: 'Retiro Disciplinario',
  academic: 'Retiro Académico',
};

const WITHDRAWAL_TYPE_DESCRIPTIONS: Record<WithdrawalType, string> = {
  voluntary: 'El alumno decide retirarse del programa por voluntad propia.',
  administrative:
    'Retiro decidido por razones administrativas (ej. falta de documentos, problemas de pago prolongados).',
  disciplinary: 'Retiro por incumplimiento del reglamento o código de conducta del seminario.',
  academic: 'Retiro por bajo rendimiento académico o incumplimiento de requisitos académicos.',
};

export interface WithdrawStudentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (summary: WithdrawalSummary) => void;
  tenantId: string;
  admissionId: string;
  studentName: string;
}

export interface WithdrawalSummary {
  admissionId: string;
  personId: string;
  tuitionPlansWithdrawn: number;
  enrollmentsDropped: number;
  programEnrollmentWithdrawn?: boolean;
  accessRevoked: boolean;
  retainedAmount: number;
  refundAmount: number;
  zeroedInstallmentsCount: number;
  /** The plans' currency; null without plans. */
  currency: string | null;
}

// ─── Step Components ─────────────────────────────────────────────────────────

interface Step1Props {
  withdrawalType: WithdrawalType;
  onTypeChange: (val: WithdrawalType) => void;
  withdrawalReason: string;
  onReasonChange: (val: string) => void;
}

function Step1Reason({
  withdrawalType,
  onTypeChange,
  withdrawalReason,
  onReasonChange,
}: Step1Props) {
  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
          Tipo de Retiro
        </Typography>
        <Select
          id="withdraw-type-select"
          value={withdrawalType}
          onChange={(e) => onTypeChange(e.target.value as WithdrawalType)}
          fullWidth
          size="small"
          sx={{ borderRadius: 2 }}
        >
          {(Object.keys(WITHDRAWAL_TYPE_LABELS) as WithdrawalType[]).map((type) => (
            <MenuItem key={type} value={type}>
              {WITHDRAWAL_TYPE_LABELS[type]}
            </MenuItem>
          ))}
        </Select>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
          {WITHDRAWAL_TYPE_DESCRIPTIONS[withdrawalType]}
        </Typography>
      </Box>

      <Box>
        <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
          Motivo del Retiro{' '}
          <Typography component="span" color="error.main">
            *
          </Typography>
        </Typography>
        <TextField
          id="withdraw-reason-field"
          value={withdrawalReason}
          onChange={(e) => onReasonChange(e.target.value)}
          multiline
          minRows={4}
          fullWidth
          placeholder="Describe el motivo del retiro con el mayor detalle posible. Esta información quedará registrada en el historial del alumno..."
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          inputProps={{ maxLength: 1000 }}
          helperText={`${withdrawalReason.length}/1000`}
        />
      </Box>
    </Stack>
  );
}

/** Un monto en la moneda del plan. Sin moneda (no hubo planes), la cifra sola:
 *  nunca una moneda supuesta. */
function money(n: number, currency: string | null | undefined): string {
  return currency
    ? formatMoney(n, currency, { maximumFractionDigits: 0 })
    : formatNumber(n, { maximumFractionDigits: 0 });
}

/** Build effective rows by overlaying admin retentions on top of baseline impacts. */
function buildEffectiveRows(
  impacts: WithdrawalInstallmentImpactDto[],
  retentions: Record<string, number>,
): Array<WithdrawalInstallmentImpactDto & { isOverridden: boolean }> {
  return impacts.map((imp) => {
    const override = retentions[imp.installmentId];
    const retained = override !== undefined ? override : imp.retainedAmount;
    return {
      ...imp,
      retainedAmount: retained,
      refundAmount: Math.max(0, imp.paidAmount - retained),
      isOverridden: override !== undefined,
    };
  });
}

function Step2Consequences({
  keepAccess,
  onKeepAccessChange,
  studentName,
}: {
  keepAccess: boolean;
  onKeepAccessChange: (val: boolean) => void;
  studentName: string;
}) {
  const { t } = useTranslation('sis');
  const c = (k: string) => t(`withdrawStudent.consequences.${k}`);
  const consequences = [
    {
      icon: <AssignmentLateRounded sx={{ color: 'warning.main', fontSize: 20 }} />,
      label: c('admissionLabel'),
      detail: c('admissionDetail'),
    },
    {
      icon: <AccountBalanceWalletRounded sx={{ color: 'warning.main', fontSize: 20 }} />,
      label: c('tuitionLabel'),
      detail: c('tuitionDetail'),
    },
    {
      icon: <SchoolRounded sx={{ color: 'warning.main', fontSize: 20 }} />,
      label: c('sectionsLabel'),
      detail: c('sectionsDetail'),
    },
    {
      icon: <SchoolRounded sx={{ color: 'warning.main', fontSize: 20 }} />,
      label: c('programLabel'),
      detail: c('programDetail'),
    },
    {
      icon: <PersonOffRounded sx={{ color: 'info.main', fontSize: 20 }} />,
      label: c('crmLabel'),
      detail: c('crmDetail'),
    },
  ];

  return (
    <Stack spacing={3}>
      <Alert severity="warning" sx={{ borderRadius: 2 }} icon={<WarningAmberRounded />}>
        <Typography variant="subtitle2" fontWeight={700}>
          {c('warningTitle')}
        </Typography>
        <Typography variant="body2">
          <Trans
            t={t}
            i18nKey="withdrawStudent.consequences.warningBody"
            values={{ name: studentName }}
            components={{ strong: <strong /> }}
          />
        </Typography>
      </Alert>

      <Box>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          mb={1.5}
          color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}
        >
          {c('heading')}
        </Typography>
        <Stack spacing={1.5}>
          {consequences.map((c) => (
            <Paper
              key={c.label}
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                border: '1px solid rgba(255,255,255,0.07)',
                background: 'rgba(255,255,255,0.02)',
              }}
            >
              {c.icon}
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {c.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {c.detail}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Stack>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 2,
          border: keepAccess ? '1px solid rgba(99,102,241,0.35)' : '1px solid rgba(239,68,68,0.35)',
          background: keepAccess ? 'rgba(99,102,241,0.06)' : 'rgba(239,68,68,0.05)',
          transition: 'all 0.2s ease',
        }}
      >
        <FormControlLabel
          control={
            <Switch
              id="keep-access-switch"
              checked={keepAccess}
              onChange={(e) => onKeepAccessChange(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Box ml={0.5}>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                display="flex"
                alignItems="center"
                gap={0.75}
              >
                <LockPersonRounded sx={{ fontSize: 18 }} />
                {c('keepAccessLabel')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {keepAccess ? c('keepAccessOn') : c('keepAccessOff')}
              </Typography>
            </Box>
          }
          sx={{ m: 0, alignItems: 'flex-start' }}
          labelPlacement="end"
        />
      </Paper>
    </Stack>
  );
}

interface Step3FinancialProps {
  preview: WithdrawalPreview | null;
  previewLoading: boolean;
  previewError: string | null;
  retentions: Record<string, number>;
  onRetentionChange: (installmentId: string, value: number) => void;
  onResetRetentions: () => void;
}

function Step3Financial({
  preview,
  previewLoading,
  previewError,
  retentions,
  onRetentionChange,
  onResetRetentions,
}: Step3FinancialProps) {
  const { t } = useTranslation('sis');
  const currency = preview?.installmentImpacts[0]?.currency ?? null;
  const rows = useMemo(
    () => (preview ? buildEffectiveRows(preview.installmentImpacts, retentions) : []),
    [preview, retentions],
  );
  const hasOverrides = Object.keys(retentions).length > 0;
  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        const forgiven = Math.max(0, r.originalAmount - r.retainedAmount - r.refundAmount);
        return {
          original: acc.original + r.originalAmount,
          retained: acc.retained + r.retainedAmount,
          refund: acc.refund + r.refundAmount,
          forgiven: acc.forgiven + forgiven,
          zeroed: acc.zeroed + (r.retainedAmount === 0 && r.type === 'monthly' ? 1 : 0),
        };
      },
      { original: 0, retained: 0, refund: 0, forgiven: 0, zeroed: 0 },
    );
  }, [rows]);

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          Edita el monto a retener por cuota según el caso. Devolución = dinero pagado que regresa.
          Anulado = deuda perdonada (no hay movimiento de dinero).
        </Typography>
        {hasOverrides && (
          <Button
            size="small"
            onClick={onResetRetentions}
            sx={{ fontSize: '0.7rem', ml: 1, flexShrink: 0 }}
          >
            Restablecer política
          </Button>
        )}
      </Stack>

      {previewLoading && (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Calculando impacto financiero…
          </Typography>
        </Stack>
      )}

      {previewError && (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          No fue posible calcular el impacto financiero: {previewError}. Aun así puedes continuar
          con el retiro.
        </Alert>
      )}

      {preview && rows.length > 0 && (
        <>
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1.4fr 0.8fr 0.8fr 1.1fr 0.85fr 0.85fr',
                gap: 0.75,
                px: 1.5,
                py: 1,
                bgcolor: 'action.hover',
                fontSize: '0.68rem',
                fontWeight: 700,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: 0.3,
              }}
            >
              <Box>Cuota</Box>
              <Box sx={{ textAlign: 'right' }}>Original</Box>
              <Box sx={{ textAlign: 'right' }}>Pagado</Box>
              <Box sx={{ textAlign: 'right' }}>Retener</Box>
              <Box sx={{ textAlign: 'right' }}>Devolver</Box>
              <Box sx={{ textAlign: 'right' }}>Anulado</Box>
            </Box>

            {rows.map((row) => {
              const forgiven = Math.max(
                0,
                row.originalAmount - row.retainedAmount - row.refundAmount,
              );
              return (
                <Box
                  key={`${row.planId}-${row.installmentId}`}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1.4fr 0.8fr 0.8fr 1.1fr 0.85fr 0.85fr',
                    gap: 0.75,
                    px: 1.5,
                    py: 1,
                    alignItems: 'center',
                    borderTop: '1px solid rgba(255,255,255,0.05)',
                    bgcolor: row.isOverridden ? 'rgba(99,102,241,0.06)' : 'transparent',
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={600} lineHeight={1.2}>
                      {row.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.dueDate}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ textAlign: 'right' }}>
                    {money(row.originalAmount, currency)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ textAlign: 'right' }}
                    color={row.paidAmount > 0 ? 'success.main' : 'text.secondary'}
                  >
                    {row.paidAmount > 0 ? money(row.paidAmount, currency) : '—'}
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    value={row.retainedAmount}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const num = raw === '' ? 0 : Number(raw);
                      if (!isNaN(num)) {
                        const clamped = Math.max(0, Math.min(row.originalAmount, num));
                        onRetentionChange(row.installmentId, clamped);
                      }
                    }}
                    inputProps={{
                      min: 0,
                      max: row.originalAmount,
                      step: 1000,
                      style: { textAlign: 'right', fontSize: '0.8rem', padding: '6px 6px' },
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ textAlign: 'right' }}
                    color={row.refundAmount > 0 ? 'warning.main' : 'text.secondary'}
                    fontWeight={row.refundAmount > 0 ? 700 : 400}
                  >
                    {row.refundAmount > 0 ? money(row.refundAmount, currency) : money(0, currency)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ textAlign: 'right' }}
                    color={forgiven > 0 ? 'info.main' : 'text.secondary'}
                    fontWeight={forgiven > 0 ? 700 : 400}
                  >
                    {forgiven > 0 ? money(forgiven, currency) : money(0, currency)}
                  </Typography>
                </Box>
              );
            })}

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1.4fr 0.8fr 0.8fr 1.1fr 0.85fr 0.85fr',
                gap: 0.75,
                px: 1.5,
                py: 1.25,
                alignItems: 'center',
                borderTop: '2px solid rgba(255,255,255,0.1)',
                bgcolor: 'rgba(255,255,255,0.03)',
              }}
            >
              <Typography variant="body2" fontWeight={700}>
                Totales
              </Typography>
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{ textAlign: 'right' }}
                color="text.secondary"
              >
                {money(totals.original, currency)}
              </Typography>
              <Box />
              <Typography
                variant="body2"
                fontWeight={700}
                color="success.main"
                sx={{ textAlign: 'right' }}
              >
                {money(totals.retained, currency)}
              </Typography>
              <Typography
                variant="body2"
                fontWeight={700}
                color={totals.refund > 0 ? 'warning.main' : 'text.secondary'}
                sx={{ textAlign: 'right' }}
              >
                {money(totals.refund, currency)}
              </Typography>
              <Typography
                variant="body2"
                fontWeight={700}
                color={totals.forgiven > 0 ? 'info.main' : 'text.secondary'}
                sx={{ textAlign: 'right' }}
              >
                {money(totals.forgiven, currency)}
              </Typography>
            </Box>
          </Paper>

          {totals.refund > 0 && (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              {t('withdrawStudent.financial.refundNotice', {
                amount: money(totals.refund, currency),
              })}
            </Alert>
          )}
          {totals.forgiven > 0 && totals.refund === 0 && (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {t('withdrawStudent.financial.forgivenNotice', {
                amount: money(totals.forgiven, currency),
              })}
            </Alert>
          )}
        </>
      )}
    </Stack>
  );
}

interface Step3Props {
  studentName: string;
  confirmation: string;
  onConfirmationChange: (val: string) => void;
}

function Step3Confirm({ studentName, confirmation, onConfirmationChange }: Step3Props) {
  return (
    <Stack spacing={3}>
      <Alert severity="error" icon={<WarningAmberRounded />} sx={{ borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          Confirmación final requerida
        </Typography>
        <Typography variant="body2">
          Para confirmar el retiro, escribe el nombre completo del alumno exactamente como aparece:
        </Typography>
        <Typography variant="body2" fontWeight={700} sx={{ mt: 0.5 }}>
          « {studentName} »
        </Typography>
      </Alert>

      <TextField
        id="withdraw-confirmation-field"
        label="Nombre del alumno"
        value={confirmation}
        onChange={(e) => onConfirmationChange(e.target.value)}
        fullWidth
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        placeholder={studentName}
        helperText="Escribe el nombre tal como aparece arriba para habilitar el botón de confirmación."
      />
    </Stack>
  );
}

// ─── Success Screen ──────────────────────────────────────────────────────────

function SuccessScreen({
  summary,
  studentName,
}: {
  summary: WithdrawalSummary;
  studentName: string;
}) {
  const { t } = useTranslation('sis');
  const s = (k: string) => t(`withdrawStudent.success.${k}`);
  return (
    <Stack spacing={2.5} alignItems="center" py={2}>
      <CheckCircleOutlineRounded sx={{ color: 'success.main', fontSize: 56 }} />
      <Box textAlign="center">
        <Typography variant="h6" fontWeight={700} gutterBottom>
          {s('title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <Trans
            t={t}
            i18nKey="withdrawStudent.success.body"
            values={{ name: studentName }}
            components={{ strong: <strong /> }}
          />
        </Typography>
      </Box>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          width: '100%',
          borderRadius: 2,
          border: '1px solid rgba(16,185,129,0.25)',
          background: 'rgba(16,185,129,0.05)',
        }}
      >
        <Stack spacing={0.75}>
          <Typography variant="body2">
            📋 <strong>{s('plans')}</strong> {summary.tuitionPlansWithdrawn}
          </Typography>
          <Typography variant="body2">
            🎓 <strong>{s('sections')}</strong> {summary.enrollmentsDropped}
          </Typography>
          <Typography variant="body2">
            🏛️ <strong>{s('program')}</strong>{' '}
            {summary.programEnrollmentWithdrawn ? s('programWithdrawn') : s('programUnchanged')}
          </Typography>
          <Typography variant="body2">
            🧾 <strong>{s('installments')}</strong> {summary.zeroedInstallmentsCount}
          </Typography>
          <Typography variant="body2">
            💰 <strong>{s('retained')}</strong> {money(summary.retainedAmount, summary.currency)}
          </Typography>
          <Typography variant="body2">
            💸 <strong>{s('refund')}</strong> {money(summary.refundAmount, summary.currency)}
            {summary.refundAmount > 0 && s('refundNote')}
          </Typography>
          <Typography variant="body2">
            🔐 <strong>{s('access')}</strong>{' '}
            {summary.accessRevoked ? s('accessRevoked') : s('accessKept')}
          </Typography>
        </Stack>
      </Paper>
    </Stack>
  );
}

// ─── Main Dialog ─────────────────────────────────────────────────────────────

const STEPS_LABELS = ['Motivo', 'Consecuencias', 'Financiero', 'Confirmar'];

export function WithdrawStudentDialog({
  open,
  onClose,
  onSuccess,
  tenantId,
  admissionId,
  studentName,
}: WithdrawStudentDialogProps) {
  const [step, setStep] = useState(0);
  const [withdrawalType, setWithdrawalType] = useState<WithdrawalType>('voluntary');
  const [withdrawalReason, setWithdrawalReason] = useState('');
  const [keepAccess, setKeepAccess] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<WithdrawalSummary | null>(null);
  const [preview, setPreview] = useState<WithdrawalPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  // Admin overrides of retained amount per installment id. Edits are applied
  // client-side over the baseline preview (no extra backend call per keystroke).
  const [retentions, setRetentions] = useState<Record<string, number>>({});

  // Fetch financial impact baseline once Step 3 (Financiero) is reached.
  // Re-uses the same baseline across edits — only refetches if admission changes.
  useEffect(() => {
    if (!open || step !== 2 || preview) return;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    admissionsApi
      .withdrawPreview({ tenantId, admissionId })
      .then((res) => {
        if (!cancelled) setPreview(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setPreviewError(errorText(err, 'Error al obtener el preview'));
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, step, tenantId, admissionId, preview]);

  const handleRetentionChange = (installmentId: string, value: number) => {
    setRetentions((prev) => ({ ...prev, [installmentId]: value }));
  };

  const handleResetRetentions = () => {
    setRetentions({});
  };

  const isStep1Valid = withdrawalReason.trim().length >= 10;
  const isConfirmValid = confirmation.trim() === studentName.trim();

  const canAdvance = () => {
    if (step === 0) return isStep1Valid;
    if (step === 1) return true;
    if (step === 2) return true;
    if (step === 3) return isConfirmValid;
    return false;
  };

  const handleNext = async () => {
    if (step < STEPS_LABELS.length - 1) {
      setStep((s) => s + 1);
      return;
    }
    // Final step: execute withdrawal
    setLoading(true);
    setError(null);
    try {
      const result = await admissionsApi.withdraw({
        tenantId,
        admissionId,
        withdrawalType,
        withdrawalReason,
        keepAccess,
        ...(Object.keys(retentions).length > 0 ? { installmentRetentions: retentions } : {}),
      });
      setSummary({
        admissionId: result.admissionId ?? admissionId,
        personId: result.personId ?? '',
        tuitionPlansWithdrawn: result.tuitionPlansWithdrawn,
        enrollmentsDropped: result.enrollmentsDropped,
        accessRevoked: result.accessRevoked,
        retainedAmount: result.retainedAmount,
        refundAmount: result.refundAmount,
        zeroedInstallmentsCount: result.zeroedInstallmentsCount,
        currency: result.currency ?? null,
      });
      onSuccess?.({
        admissionId: result.admissionId ?? admissionId,
        personId: result.personId ?? '',
        tuitionPlansWithdrawn: result.tuitionPlansWithdrawn,
        enrollmentsDropped: result.enrollmentsDropped,
        accessRevoked: result.accessRevoked,
        retainedAmount: result.retainedAmount,
        refundAmount: result.refundAmount,
        zeroedInstallmentsCount: result.zeroedInstallmentsCount,
        currency: result.currency ?? null,
      });
    } catch (err: unknown) {
      const msg = errorText(err, 'Error al procesar el retiro');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    // Reset state
    setStep(0);
    setWithdrawalType('voluntary');
    setWithdrawalReason('');
    setKeepAccess(false);
    setConfirmation('');
    setError(null);
    setSummary(null);
    setPreview(null);
    setPreviewError(null);
    setRetentions({});
    onClose();
  };

  const nextLabel = step < STEPS_LABELS.length - 1 ? 'Continuar' : 'Registrar Retiro';

  return (
    <Dialog
      open={open}
      onClose={summary ? handleClose : undefined}
      maxWidth={step === 2 ? 'md' : 'sm'}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: '1px solid rgba(239,68,68,0.2)',
        },
      }}
    >
      <DialogTitle sx={{ pb: 0 }}>
        <Stack direction="row" alignItems="center" gap={1.5}>
          <PersonOffRounded sx={{ color: 'error.main', fontSize: 26 }} />
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              Registrar Retiro del Alumno
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {studentName}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        {summary ? (
          <SuccessScreen summary={summary} studentName={studentName} />
        ) : (
          <Stack spacing={3}>
            <Stepper activeStep={step} alternativeLabel>
              {STEPS_LABELS.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <Divider />

            {step === 0 && (
              <Step1Reason
                withdrawalType={withdrawalType}
                onTypeChange={setWithdrawalType}
                withdrawalReason={withdrawalReason}
                onReasonChange={setWithdrawalReason}
              />
            )}
            {step === 1 && (
              <Step2Consequences
                keepAccess={keepAccess}
                onKeepAccessChange={setKeepAccess}
                studentName={studentName}
              />
            )}
            {step === 2 && (
              <Step3Financial
                preview={preview}
                previewLoading={previewLoading}
                previewError={previewError}
                retentions={retentions}
                onRetentionChange={handleRetentionChange}
                onResetRetentions={handleResetRetentions}
              />
            )}
            {step === 3 && (
              <Step3Confirm
                studentName={studentName}
                confirmation={confirmation}
                onConfirmationChange={setConfirmation}
              />
            )}

            {error && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 0, gap: 1 }}>
        {summary ? (
          <Button
            id="withdraw-close-btn"
            onClick={handleClose}
            variant="contained"
            sx={{ borderRadius: 2, ml: 'auto' }}
          >
            Cerrar
          </Button>
        ) : (
          <>
            <Button
              id="withdraw-cancel-btn"
              onClick={handleClose}
              disabled={loading}
              color="inherit"
              sx={{ borderRadius: 2 }}
            >
              Cancelar
            </Button>
            <Box sx={{ flex: 1 }} />
            {step > 0 && (
              <Button
                id="withdraw-back-btn"
                onClick={() => setStep((s) => s - 1)}
                disabled={loading}
                sx={{ borderRadius: 2 }}
              >
                Atrás
              </Button>
            )}
            <Button
              id="withdraw-next-btn"
              onClick={handleNext}
              variant="contained"
              color={step === STEPS_LABELS.length - 1 ? 'error' : 'primary'}
              disabled={!canAdvance() || loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{ borderRadius: 2, fontWeight: 700 }}
            >
              {loading ? 'Procesando...' : nextLabel}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
